import Cookies from 'js-cookie';
import type { ApiResponse } from '@/types';
import { handleSessionExpired } from './sessionGuard';

const FALLBACK_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:9001';
const PODUS_SYSTEM_KEY = 'G@V3l2023-new-podus-v001233-SyS';
const API_BASE_KEY = 'md_api_base';

export function getBaseUrl(): string {
    const stored = localStorage.getItem(API_BASE_KEY);
    return stored || FALLBACK_BASE_URL;
}

function getToken(): string | null {
    return Cookies.get('p-ai-accessToken') ?? null;
}

function buildHeaders(isMultipart = false): HeadersInit {
    const token = getToken();
    const headers: Record<string, string> = {};

    if (token) {
        headers['Authorization'] = `Bearer ${PODUS_SYSTEM_KEY}:${token}`;
    }
    if (!isMultipart) {
        headers['Content-Type'] = 'application/json';
    }

    return headers;
}

async function handleResponse<T>(res: Response): Promise<ApiResponse<T>> {
    const json = await res.json();
    if (!res.ok) {
        const message: string | undefined = json?.response?.message;
        if (handleSessionExpired(res.status, message)) {
            throw new Error(message ?? 'Session expired');
        }
        throw new Error(message ?? 'Something went wrong');
    }
    return json as ApiResponse<T>;
}

export const api = {
    async get<T>(path: string): Promise<ApiResponse<T>> {
        const res = await fetch(`${getBaseUrl()}${path}`, {
            method: 'GET',
            headers: buildHeaders(),
        });
        return handleResponse<T>(res);
    },

    async post<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
        const res = await fetch(`${getBaseUrl()}${path}`, {
            method: 'POST',
            headers: buildHeaders(),
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        return handleResponse<T>(res);
    },

    async patch<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
        const res = await fetch(`${getBaseUrl()}${path}`, {
            method: 'PATCH',
            headers: buildHeaders(),
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        return handleResponse<T>(res);
    },

    async put<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
        const res = await fetch(`${getBaseUrl()}${path}`, {
            method: 'PUT',
            headers: buildHeaders(),
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        return handleResponse<T>(res);
    },

    async delete<T>(path: string): Promise<ApiResponse<T>> {
        const res = await fetch(`${getBaseUrl()}${path}`, {
            method: 'DELETE',
            headers: buildHeaders(),
        });
        return handleResponse<T>(res);
    },

    async upload<T>(path: string, formData: FormData): Promise<ApiResponse<T>> {
        const res = await fetch(`${getBaseUrl()}${path}`, {
            method: 'POST',
            headers: buildHeaders(true),
            body: formData,
        });
        return handleResponse<T>(res);
    },
};
